import React, { useReducer, useState, useEffect } from 'react';
import * as qs from 'qs';
import { useHistory, useLocation } from 'react-router-dom';
import { checkAuthState, signIn } from '../../../utils/APIUtils';
import { LOGOUT, USER } from '../../../constants/CommonConst';
import { notificationManager } from '../../../utils/NotificationUtils';
import {
  convertUserToCognitoFormat,
  exchangeCodeForToken,
  getUser,
  isSSOConfigured,
} from '../../../utils/PropertyAPIUtils';
import TextField from '@material-ui/core/TextField';
import { InputAdornment } from '@material-ui/core';
import PersonIcon from '@material-ui/icons/Person';
import VisibilityIcon from '@material-ui/icons/Visibility';
import './pre-login.scss';
import { Button, CircularProgress } from '@material-ui/core';
import { VisibilityOff } from '@material-ui/icons';
import { handleValidation } from '../../../utils/formUtils';
import { useDispatch } from 'react-redux';
import { setAllBrands, setAllGroups, setAllHotels } from '../../property/propertySlice';
import { decryptLocalStorage, encryptLocalStorage } from '../../../utils/authUtils';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../utils/loaderSlice';
import { decodeJWT } from '../../../utils/RBACUtil';
import { SSO_CLIENT_ID } from '../../../env/Configurations';
import Loader from '../../../shared/loader/Loader';

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};
const PreLogin = (props) => {
  const query = useQuery();
  const history = useHistory();
  const code = query.get('code') || '';
  const [fields, setFields] = useReducer((state, newState) => ({ ...state, ...newState }), {
    email: {
      value: '',
      fieldName: 'Email',
      errors: {},
      validations: ['required', 'validEmail'],
    },
  });

  const [loading, setLoading] = useState(!!code);
  const [showBox, setShowBox] = useState(!code);
  const [title, setTitle] = useState('Log in');
  const [pendingSSOLogout, setPendingSSOLogout] = useState(null);
  const [ssoLoginSuccess, setSsoLoginSuccess] = useState(false);
  const [mismatchedSSOEmail, setMismatchedSSOEmail] = useState(null);
  const dispatch = useDispatch();

  const handleInputChange = (e, type) => {
    const inputValue = e.target.value;
    const currField = { ...fields }[type];

    currField.value = inputValue;
    delete currField.errors.custom;
    handleValidation(currField);

    setFields({
      [type]: currField,
    });
  };

  useEffect(() => {
    if (sessionStorage.getItem('logout_success')) {
      sessionStorage.removeItem('logout_success');
      notificationManager('success', 'User logged out successfully');
    }

    const handlePageShow = (event) => {
      if (event.persisted) {
        setLoading(false);
        setShowBox(true);
        dispatch(stepDownLoaderCount());
      }
    };
    window.addEventListener('pageshow', handlePageShow);

    const handleStorageChange = (e) => {
      if (e.key === 'user' && e.newValue) {
        history.push('/');
      }
    };
    window.addEventListener('storage', handleStorageChange);

    (async () => {
      const currUser = await checkAuthState();
      const loggedinUser = decryptLocalStorage(USER);

      if (loggedinUser && currUser && currUser.signInUserSession.isValid()) {
        history.push('/');
      }
    })();

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const validate = () => {
    let isValid = true;
    const updatedFields = {};
    for (let field in fields) {
      const currField = { ...fields }[field];
      if (handleValidation(currField)) isValid = false;
      updatedFields[field] = currField;
    }
    setFields(updatedFields);
    return isValid;
  };

  const isSSOLogin = async (email) => {
    let isConfigured = false;
    try {
      const response = await isSSOConfigured({ email });
      const redirectUri = `${window.location.origin}/prelogin`;
      if (response?.status === 'ok' && response?.data?.configured) {
        if (!response?.data?.userExists) {
          notificationManager('danger', 'This email is not registered');
          setFields({ email: { ...fields.email, errors: { custom: 'This email is not registered' } } });
          setLoading(false);
          setShowBox(true);
          isConfigured = true;
        } else if (response?.data?.samlEnabled === true) {
          let loginUrl = response?.data?.loginUrl;
          let logoutUrl = response?.data?.logoutUrl;

          if (!loginUrl) {
            notificationManager('danger', 'SSO login URL is not configured');
            setFields({ email: { ...fields.email, errors: { custom: 'User Not Found' } } });
            setLoading(false);
            setShowBox(true);
            isConfigured = true;
            return isConfigured;
          }
          if (logoutUrl.endsWith('logout_uri=')) {
            logoutUrl += encodeURIComponent(redirectUri);
            localStorage.setItem(LOGOUT, logoutUrl);
          }
          if (loginUrl.endsWith('redirect_uri=')) {
            loginUrl += encodeURIComponent(redirectUri);
            loginUrl += '&prompt=login&login_hint=' + encodeURIComponent(email);
            sessionStorage.setItem('prelogin_email', email);
            isConfigured = true;
            window.location.href = loginUrl;
          }
        } else {
          // const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          // const locale = Intl.DateTimeFormat().resolvedOptions().locale;
          // const azureRes = await azureLogin({
          //   email: email,
          //   userAgent: navigator?.appVersion || '',
          //   screenResolution: '1920x1080',
          //   timezone: timeZone,
          //   language: locale,
          //   pluginsCount: 1,
          //   postSigninUrl: redirectUri,
          // });
          // if (azureRes?.data?.azureAuthenticationEnabled && azureRes?.data?.userExists === false) {
          //   toast.error('Azure login failed - User is not authorized');
          // } else if (azureRes?.data?.azureAuthenticationEnabled && azureRes?.data?.userExists) {
          //   azureRes?.data?.azureLoginURL && router.replace(azureRes?.data?.azureLoginURL);
          //   isConfigured = true;
          // }
        }
      } else {
        isConfigured = false;
      }
    } catch (error) {
      console.error('SSO Configuration check failed:', error);
      notificationManager('danger', 'Unable to verify SSO configuration. Please try again.');
      setLoading(false);
      setShowBox(true);
      isConfigured = true;
    }

    return isConfigured;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setLoading(true);
      const email = fields.email.value.toLowerCase();
      try {
        dispatch(stepUpLoaderCount());

        if (pendingSSOLogout && mismatchedSSOEmail !== email) {
          const logoutUrl = pendingSSOLogout;
          setPendingSSOLogout(null);
          setMismatchedSSOEmail(null);
          const logoutPopup = window.open(
            'https://login.microsoftonline.com/common/wsfederation?wa=wsignout1.0',
            'sso_logout',
            'width=500,height=600'
          );
          if (logoutPopup) {
            await new Promise((resolve) => {
              setTimeout(() => {
                try { logoutPopup.location = logoutUrl; } catch (e) {}
              }, 2000);

              const pollTimer = setInterval(() => {
                try {
                  if (logoutPopup.closed) {
                    clearInterval(pollTimer);
                    resolve();
                    return;
                  }
                  if (logoutPopup.location?.origin === window.location.origin) {
                    clearInterval(pollTimer);
                    logoutPopup.close();
                    resolve();
                  }
                } catch (e) {
                  
                }
              }, 500);
              
              setTimeout(() => {
                clearInterval(pollTimer);
                try { if (!logoutPopup.closed) logoutPopup.close(); } catch (e) {}
                resolve();
              }, 15000);
            });
            notificationManager('success', 'Previous SSO session signed out successfully.');
          }
        } else if (pendingSSOLogout && mismatchedSSOEmail === email) {
          
          setPendingSSOLogout(null);
          setMismatchedSSOEmail(null);
        }

        if (!(await isSSOLogin(email))) {
          localStorage.removeItem(LOGOUT);
          sessionStorage.setItem('prelogin_email', email);
          history.push('/login');
          setLoading(false);
        }
      } catch (e) {
        setLoading(false);
        notificationManager('danger', 'Login failed - ' + e.message);
      } finally {
        dispatch(stepDownLoaderCount());
      }
    }
  };

  useEffect(() => {
    (async () => {
      if (code) {
       
        if (sessionStorage.getItem('sso_processed_code') === code) {
          sessionStorage.removeItem('sso_processed_code');
          history.replace('/prelogin');
          return;
        }

        setLoading(true);
        setShowBox(false);
        dispatch(stepUpLoaderCount());

        sessionStorage.setItem('sso_processed_code', code);

        const user = await exchangeCodeForToken(code);
        if (user) {
          const ssoEmail = (user.attributes?.email || '').toLowerCase();
          const preloginEmail = (sessionStorage.getItem('prelogin_email') || '').toLowerCase();

          if (preloginEmail && ssoEmail && preloginEmail !== ssoEmail) {
            sessionStorage.removeItem('prelogin_email');
            sessionStorage.removeItem('sso_processed_code');

            notificationManager('danger', 'Email mismatch: The SSO account email does not match the email entered on the login page. Please try again.');

            const ssoLogoutUrl = localStorage.getItem(LOGOUT);

            localStorage.removeItem('user');
            localStorage.removeItem('lang');
            localStorage.removeItem(LOGOUT);
            Object.keys(localStorage)
              .filter((k) => k.includes('CognitoIdentityServiceProvider'))
              .forEach((k) => localStorage.removeItem(k));

            if (ssoLogoutUrl) {
              setPendingSSOLogout(ssoLogoutUrl);
            }
            setMismatchedSSOEmail(ssoEmail);

            dispatch(stepDownLoaderCount());
            setLoading(false);
            setShowBox(true);
            history.replace('/prelogin');
            return;
          }

          sessionStorage.removeItem('sso_processed_code');
          sessionStorage.removeItem('prelogin_email');
          const userPoolId = SSO_CLIENT_ID;
          const username = user.attributes.userId;
          const userData = convertUserToCognitoFormat(user);
          const idToken = user.signInUserSession?.idToken?.jwtToken;
          const accessToken = user.access_token;
          const refreshToken = user.refresh_token;
          const clockDrift = -1;

          const baseKey = `CognitoIdentityServiceProvider.${userPoolId}`;
          localStorage.setItem(`${baseKey}.LastAuthUser`, username);
          localStorage.setItem(`${baseKey}.${username}.idToken`, idToken);
          localStorage.setItem(`${baseKey}.${username}.accessToken`, accessToken);
          localStorage.setItem(`${baseKey}.${username}.refreshToken`, refreshToken);
          localStorage.setItem(`${baseKey}.${username}.userData`, JSON.stringify(userData));
          localStorage.setItem(`${baseKey}.${username}.clockDrift`, clockDrift.toString());

          const result = await getUser(username);
          encryptLocalStorage(USER, result.data.getUserById);

          setSsoLoginSuccess(true);
          history.push('/');
          setTimeout(() => dispatch(stepDownLoaderCount()), 500);
        } else {
          sessionStorage.removeItem('sso_processed_code');
          dispatch(stepDownLoaderCount());
          setLoading(false);
          setShowBox(true);
        }
      }
    })();
  }, [code]);

  if (ssoLoginSuccess || (code && loading)) {
    return <Loader />;
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className='row d-flex justify-content-center align-items-center mb-3 content mx-0'>
        <div className='header-row d-flex justify-content-center'>
          <span className='welcome-title'>{title}</span>
          <span>&nbsp;to</span>
          <span className='elevate ms-1'> &nbsp;elevate</span>
        </div>
      </div>
      <div className='row d-flex justify-content-center align-items-center  mx-0'>
        <div className='col-md-5 mb-3 d-flex justify-content-center'>
          {showBox && (
            <TextField
              id='email'
              label='Email'
              required
              className='col-md-6'
              value={fields.email.value}
              onChange={(e) => handleInputChange(e, 'email')}
              error={fields.email.errors.required || fields.email.errors.validEmail || fields.email.errors.custom}
              helperText={fields.email.errors.required || fields.email.errors.validEmail || fields.email.errors.custom}
              InputProps={
                title === 'Log in' && {
                  endAdornment: (
                    <InputAdornment position='end'>
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }
              }
            />
          )}
        </div>
      </div>
      <div className='row d-flex justify-content-center align-items-center mt-4 mx-0'>
        <div className='col-md-12 mb-3 d-flex justify-content-center'>
          {loading ? (
            <CircularProgress color='primary' />
          ) : (
            <Button variant='contained' type='submit' color='primary' size='large'>
              CONTINUE
            </Button>
          )}
        </div>
      </div>
    </form>
  );
};

export default PreLogin;
